import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { 
    Search, Download, FileText, BarChart2, Calendar, 
    FileDown, X, ChevronRight, CheckCircle, Edit2, 
    Printer, Filter, Send, Target, LayoutDashboard,
    Clock, Mail, Share2, MoreVertical, History
} from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import ReportTemplate from '../../components/ReportTemplate';

const FILTER_TABS = ['All Reports', 'Patient Specific', 'System Logs'];

export default function Reports() {
    const { assessments, profiles } = useData();
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('All Reports');

    // UI State
    const [printingReport, setPrintingReport] = useState(null);
    const [selectedReportId, setSelectedReportId] = useState(null);

    // Merge real assessments with some professional defaults
    const reportData = useMemo(() => {
        // Robust safety mapping
        const assessmentReports = (assessments || []).map(a => {
            if (!a || typeof a !== 'object') return null;
            return {
                id: a.id || Math.random(),
                title: `${a.type || 'Assessment'} - Clinical Report`,
                desc: `Clinical outcome for ${a.patientName || 'N/A'} submitted by ${a.submitterRole || 'Clinician'}.`,
                date: a.date || new Date().toISOString(),
                status: a.status || 'Finalized',
                priority: a.score > 25 ? 'High' : 'Normal',
                type: 'Patient Specific', // Keeping original type for filtering
                format: 'PDF', // Keeping original format
                icon: BarChart2, // Keeping original icon
                patientName: a.patientName, // Keeping patientName for display/filtering
                raw: a
            };
        }).filter(Boolean);

        const systemReports = [
            {
                id: 'sys-1',
                title: 'Institution Growth Audit Q3',
                desc: 'Aggregated longitudinal data for the current clinical cohort.',
                type: 'System Logs',
                format: 'CSV / PDF',
                icon: LayoutDashboard,
                date: new Date().toISOString()
            },
            {
                id: 'sys-2',
                title: 'Assessor Compliance Log',
                desc: 'Verification status of all professional attestations and evaluations.',
                type: 'System Logs',
                format: 'PDF',
                icon: CheckCircle,
                date: new Date().toISOString()
            }
        ];

        return [...assessmentReports, ...systemReports].sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [assessments]);

    const filtered = useMemo(() => {
        return reportData.filter(r => {
            const matchesSearch = searchQuery === '' ||
                r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (r.patientName || '').toLowerCase().includes(searchQuery.toLowerCase());
            const matchesTab =
                activeTab === 'All Reports' ||
                (activeTab === 'Patient Specific' && r.type === 'Patient Specific') ||
                (activeTab === 'System Logs' && r.type === 'System Logs');
            return matchesSearch && matchesTab;
        });
    }, [reportData, searchQuery, activeTab]);

    const handlePrint = (report) => {
        if (report.raw) {
            setPrintingReport(report.raw);
        } else {
            // Mock data for system reports
            setPrintingReport({
                patientName: report.patientName || 'Institutional Audit',
                type: report.title,
                date: report.date,
                score: 85,
                questions: ['Data Integrity', 'Clinical Compliance', 'Stakeholder Engagement', 'Growth Velocity'],
                answers: { 0: 4, 1: 4, 2: 3, 3: 4 }
            });
        }
        
        setTimeout(() => {
            window.print();
            setPrintingReport(null);
        }, 800);
    };

    return (
        <div className="space-y-10 font-inter pb-20">
            {/* Header Area */}
            <div className={`flex flex-col md:flex-row md:items-center justify-between gap-8 ${printingReport ? 'print:hidden' : ''}`}>
                <div className="flex items-center gap-6">
                    <div className="h-16 w-16 rounded-[1.5rem] bg-brand-600 flex items-center justify-center text-white shadow-premium">
                        <FileDown className="h-8 w-8" />
                    </div>
                    <div>
                        <h1 className="text-4xl font-black text-slate-900 font-outfit tracking-tight uppercase">Reports Hub</h1>
                        <p className="text-slate-400 font-bold text-[10px] uppercase tracking-[0.3em] mt-1 italic">Authorized Personnel Only • Clinical Documentation</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <Button variant="outline" className="h-14 rounded-2xl border-slate-200 bg-white font-black text-xs uppercase tracking-widest text-slate-600 shadow-sm" onClick={() => alert('Opening secure transfer log...')}>
                        <History className="mr-3 h-4 w-4" /> Transfer Log
                    </Button>
                    <Button className="h-14 rounded-2xl px-8 bg-slate-900 hover:bg-slate-800 text-white font-black text-xs uppercase tracking-widest shadow-premium transition-all" onClick={() => alert('Starting bulk export protocol...')}>
                        <Download className="mr-3 h-4 w-4" /> Bulk Export
                    </Button>
                </div>
            </div>

            {/* Content Control */}
            <Card className={`border-0 shadow-soft overflow-hidden rounded-[2.5rem] bg-white border border-slate-50 ${printingReport ? 'print:hidden' : ''}`}>
                <CardContent className="p-8 flex flex-col lg:flex-row gap-8 items-center">
                    <div className="relative flex-1 w-full group">
                        <Search className="absolute left-6 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-300 group-focus-within:text-brand-500 transition-colors" />
                        <input 
                            className="w-full h-16 pl-16 pr-6 rounded-[1.5rem] bg-slate-50 border-none focus:ring-2 focus:ring-brand-500 text-slate-900 placeholder:text-slate-300 font-bold transition-all" 
                            placeholder="Identify report via Title, Patient, or Clinical ID..." 
                            value={searchQuery} 
                            onChange={e => setSearchQuery(e.target.value)} 
                        />
                    </div>
                    <div className="flex items-center bg-slate-100/50 rounded-[1.5rem] p-2 border border-slate-100">
                        {FILTER_TABS.map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-8 py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${activeTab === tab ? 'bg-white text-brand-600 shadow-premium' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Clinical Reports Matrix */}
            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 ${printingReport ? 'print:hidden' : ''}`}>
                {filtered.map((report) => (
                    <Card key={report.id} className="border-0 shadow-soft rounded-[3rem] bg-white border border-slate-50 hover:shadow-premium hover:-translate-y-2 transition-all group overflow-hidden flex flex-col min-h-[420px]">
                        <div className="p-10 flex-1 flex flex-col">
                            <div className="flex justify-between items-start mb-10">
                                <div className="h-16 w-16 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-300 group-hover:bg-brand-50 group-hover:text-brand-600 transition-all border border-slate-100 shadow-inner">
                                    <report.icon className="h-7 w-7" />
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <Badge className="bg-slate-900 text-white border-0 px-4 py-1.5 text-[9px] font-black uppercase tracking-widest rounded-lg">{report.format}</Badge>
                                    <span className="text-[10px] font-black text-slate-300 uppercase">{new Date(report.date).toLocaleDateString()}</span>
                                </div>
                            </div>
                            
                            <h3 className="text-2xl font-black text-slate-900 group-hover:text-brand-600 transition-colors font-outfit uppercase tracking-tight mb-4 leading-tight">{report.title}</h3>
                            <p className="text-slate-400 font-medium text-sm leading-relaxed mb-10 flex-1 italic">"{report.desc}"</p>
                            
                            <div className="pt-8 border-t border-slate-50 space-y-6">
                                <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-[0.2em]">
                                    <span className="text-slate-300">Target Subject</span>
                                    <span className="text-slate-900">{report.patientName || 'Clinical Global'}</span>
                                </div>
                                    <div className="flex gap-2">
                                        <Button variant="ghost" size="icon" className="h-10 w-10 text-slate-300 hover:text-brand-600 hover:bg-brand-50 transition-all rounded-xl" onClick={() => alert('Notification protocol initiated.')}>
                                            <Send className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="h-10 w-10 text-slate-300 hover:text-slate-900 hover:bg-slate-50 transition-all rounded-xl" onClick={() => handlePrint(report)}>
                                            <Printer className="h-4 w-4" />
                                        </Button>
                                    </div>
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            {/* Print View Overlay */}
            {printingReport && (
                <div className="fixed inset-0 z-[9999] bg-white overflow-y-auto">
                    <ReportTemplate assessmentData={printingReport} />
                </div>
            )}

            {/* Empty State */}
            {filtered.length === 0 && (
                <div className="py-40 text-center">
                    <div className="h-32 w-32 bg-slate-50 border border-slate-100 rounded-[3rem] flex items-center justify-center mx-auto mb-10 shadow-inner">
                        <X className="h-12 w-12 text-slate-200" />
                    </div>
                    <h3 className="text-3xl font-black text-slate-900 font-outfit uppercase">No Records Found</h3>
                    <p className="text-slate-400 font-medium mt-4">Broaden your clinical search parameters or initialize a new builder protocol.</p>
                </div>
            )}
        </div>
    );
}
